/**
 * Shared filter normalization/building for claims dashboard.
 *
 * Eliminates duplicated filter transformation logic that was previously
 * repeated across getClaimsDownloadReplaceParams() and getClaimsList().
 */
import { CLIENT_STATUS_GROUPINGS } from '../../claimsUtils';

/**
 * Normalizes date filter values to start-of-day / end-of-day epoch timestamps.
 */
const normalizeDateFilter = dateFilters => {
  let fromDate = dateFilters && dateFilters.fromDate;
  if (fromDate) {
    fromDate = new Date(
      new Date(parseInt(fromDate, 10)).setHours(0, 0, 0, 0)
    ).getTime();
  } else {
    fromDate = null;
  }

  let toDate = dateFilters && dateFilters.toDate;
  if (toDate) {
    toDate = new Date(
      new Date(parseInt(toDate, 10)).setHours(23, 59, 59, 999)
    ).getTime();
  } else {
    toDate = null;
  }

  return { fromDate, toDate };
};

/**
 * Normalizes status filter values from the multi-select UI.
 * Expands CLIENT_STATUS_GROUPINGS for client/RP users.
 */
const normalizeStatusFilter = (statusFilter, isClientUser) => {
  let statuses = [];
  if (!statusFilter) return statuses;

  let rawVals = [];
  if (Array.isArray(statusFilter)) {
    const hasAllSelected = statusFilter.some(
      s => s.value === '' || (s.label && s.label.toLowerCase() === 'all')
    );
    if (!hasAllSelected) {
      rawVals = statusFilter
        .map(s => s.value)
        .filter(val => val !== '' && val !== null && val !== undefined);
    }
  } else if (statusFilter.value) {
    rawVals = [statusFilter.value];
  }

  if (isClientUser) {
    rawVals.forEach(val => {
      if (CLIENT_STATUS_GROUPINGS[val]) {
        statuses.push(...CLIENT_STATUS_GROUPINGS[val]);
      } else {
        statuses.push(val);
      }
    });
  } else {
    statuses = rawVals;
  }

  return statuses;
};

/**
 * Normalizes reason filter values from the multi-select UI.
 * Returns an array of numeric reason IDs.
 */
const normalizeReasonFilter = reasonFilter => {
  let reasonIds = [];
  if (!reasonFilter) return reasonIds;

  if (Array.isArray(reasonFilter)) {
    const hasAllReason = reasonFilter.some(
      r =>
        r.id === 0 ||
        r.id === '' ||
        (r.name && r.name.toLowerCase() === 'all') ||
        (r.reason && r.reason.toLowerCase() === 'all')
    );
    if (!hasAllReason) {
      reasonIds = reasonFilter
        .map(r => parseInt(r.id, 10))
        .filter(
          id => !Number.isNaN(id) && id !== 0 && id !== null && id !== undefined
        );
    }
  } else if (reasonFilter.id) {
    const idVal = parseInt(reasonFilter.id, 10);
    if (!Number.isNaN(idVal) && idVal !== 0) {
      reasonIds = [idVal];
    }
  }

  return reasonIds;
};

/**
 * Normalizes a generic multi-select filter t hat uses { value, label } options.
 * Returns an array of selected values (strings).
 */
const normalizeMultiSelectFilter = filter => {
  let values = [];
  if (!filter) return values;

  if (Array.isArray(filter)) {
    const hasAllSelected = filter.some(
      s => s.value === '' || (s.label && s.label.toLowerCase() === 'all')
    );
    if (!hasAllSelected) {
      values = filter
        .map(s => s.value)
        .filter(val => val !== '' && val !== null && val !== undefined);
    }
  } else if (filter.value) {
    values = [filter.value];
  }

  return values;
};

/**
 * Normalizes recoverable filter values.
 * Maps 'YES'/'NO' strings to boolean values.
 */
const normalizeRecoverableFilter = recoverableFilter => {
  let values = [];
  if (!recoverableFilter) return values;

  if (Array.isArray(recoverableFilter)) {
    const hasAllSelected = recoverableFilter.some(
      r => r.value === '' || (r.label && r.label.toLowerCase() === 'all')
    );
    if (!hasAllSelected) {
      values = recoverableFilter
        .map(r => r.value === 'YES')
        .filter(val => val !== null && val !== undefined);
    }
  } else if (recoverableFilter.value) {
    values = [recoverableFilter.value === 'YES'];
  }

  return values;
};

/**
 * Normalizes clientCode/rpCode filters for SAM/KAM dropdown selections.
 * If "ALL" is selected, returns an empty array. Otherwise returns list of values.
 */
const normalizeSamKamFilter = filter => {
  let values = [];
  if (!filter) return values;

  if (Array.isArray(filter)) {
    const hasAllSelected = filter.some(
      s =>
        s.value === '' ||
        (s.name && s.name.toLowerCase() === 'all') ||
        (s.label && s.label.toLowerCase() === 'all')
    );
    if (!hasAllSelected) {
      values = filter
        .map(s => {
          const valStr = s.value !== undefined ? s.value : s.name;
          const parsed = parseInt(valStr, 10);
          return !Number.isNaN(parsed) ? parsed : valStr;
        })
        .filter(val => val !== '' && val !== null && val !== undefined);
    }
  } else if (filter.value) {
    if (filter.value !== '' && (filter.name || '').toLowerCase() !== 'all') {
      const valStr = filter.value;
      const parsed = parseInt(valStr, 10);
      values = [!Number.isNaN(parsed) ? parsed : valStr];
    }
  } else if (typeof filter === 'string' && filter.trim() !== '') {
    if (filter.trim().toLowerCase() !== 'all') {
      const valStr = filter.trim();
      const parsed = parseInt(valStr, 10);
      values = [!Number.isNaN(parsed) ? parsed : valStr];
    }
  }

  return values;
};

/**
 * Builds the complete claim filter DTO from dashboard state.
 *
 * @param {Object} claimsDashboardState - the claim dashboard Redux state slice
 * @param {boolean} isClientUser - whether the current user is a client/RP role
 * @returns {{ claimFilterDTO: Object, hasFilter: boolean, fromDate: number|null, toDate: number|null, statuses: string[], reasonIds: number[], settlementMechanisms: string[], recoverableValues: boolean[] }}
 */
export const buildClaimFilters = (claimsDashboardState, isClientUser) => {
  const { filters } = claimsDashboardState;

  const { fromDate, toDate } = normalizeDateFilter(filters.dateFilters);
  const statuses = normalizeStatusFilter(filters.status, isClientUser);
  const reasonIds = normalizeReasonFilter(filters.reason);
  const settlementMechanisms = normalizeMultiSelectFilter(
    filters.settlementMechanism
  );
  const recoverableValues = normalizeRecoverableFilter(filters.recoverable);

  const clientCodes = normalizeSamKamFilter(filters.clientCode);
  const rpCodes = normalizeSamKamFilter(filters.rpCode);

  const claimFilterDTO = {
    statuses: statuses.length > 0 ? statuses : null,
    reasonIds: reasonIds.length > 0 ? reasonIds : null,
    settlementMechanisms:
      settlementMechanisms.length > 0 ? settlementMechanisms : null,
    recoverable: recoverableValues.length > 0 ? recoverableValues : null,
    fromDate,
    toDate,
    clientIds: clientCodes.length > 0 ? clientCodes : null,
    rpUserIds: rpCodes.length > 0 ? rpCodes : null
  };

  const hasFilter =
    statuses.length > 0 ||
    reasonIds.length > 0 ||
    settlementMechanisms.length > 0 ||
    recoverableValues.length > 0 ||
    fromDate !== null ||
    toDate !== null ||
    clientCodes.length > 0 ||
    rpCodes.length > 0;

  return {
    claimFilterDTO,
    hasFilter,
    fromDate,
    toDate,
    statuses,
    reasonIds,
    settlementMechanisms,
    recoverableValues
  };
};

/**
 * Builds URL replace params array for download endpoints.
 * This is a thin wrapper over buildClaimFilters for backward-compat with
 * the replaceParamsArr pattern used in the download action.
 */
export const buildClaimDownloadParams = (
  claimsDashboardState,
  isClientUser
) => {
  const { fromDate, toDate, statuses, reasonIds } = buildClaimFilters(
    claimsDashboardState,
    isClientUser
  );

  const reasonIdsVal = reasonIds.length > 0 ? reasonIds.join(',') : '';
  const clientCodes = normalizeSamKamFilter(
    claimsDashboardState.filters.clientCode
  );
  const rpCodes = normalizeSamKamFilter(claimsDashboardState.filters.rpCode);

  return [
    {
      key: 'statuses',
      val: statuses.length > 0 ? statuses.join(',') : ''
    },
    {
      key: 'reasonIds',
      val: reasonIdsVal
    },
    { key: 'fromDate', val: fromDate },
    { key: 'toDate', val: toDate },
    { key: 'cn', val: (claimsDashboardState.search || '').trim() },
    {
      key: 'clientIds',
      val: clientCodes.length > 0 ? clientCodes.join(',') : ''
    },
    {
      key: 'rpUserIds',
      val: rpCodes.length > 0 ? rpCodes.join(',') : ''
    }
  ].filter(
    item => item.val !== null && item.val !== undefined && item.val !== ''
  );
};
